import axios, { BASE_URL } from './config';

const baseURL = '/learner';

// Badge Endpoints
export const learnerBadgeAPI = {
  // Get all available badges
  getBadges: () => axios.get(`${baseURL}/badges`),
  
  // Get my badges
  getMyBadges: () => axios.get(`${baseURL}/badges/my-badges`),
  
  // Award welcome badge
  awardWelcomeBadge: () => axios.post(`${baseURL}/welcome`),
};

// Course Browsing Endpoints
export const learnerCourseAPI = {
  // Get all courses with filtering and pagination
  getCourses: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    return axios.get(`${baseURL}/courses?${queryParams.toString()}`);
  },
  
  // Get course details
  getCourseDetails: (courseId) => axios.get(`${baseURL}/courses/${courseId}`),
  
  // Start course
  startCourse: (courseId) => axios.post(`${baseURL}/courses/${courseId}/start`),
  
  // Complete course
  completeCourse: (courseId) => axios.post(`${baseURL}/courses/${courseId}/complete`),
  
  // Rate course
  rateCourse: (courseId, rating, review = '') => 
    axios.post(`${baseURL}/courses/${courseId}/rate`, { rating, review }),
  
  // Get course ratings
  getCourseRatings: (courseId, params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    return axios.get(`${baseURL}/courses/${courseId}/ratings?${queryParams.toString()}`);
  },
  
  // Flag course
  flagCourse: (courseId, reason) => 
    axios.post(`${baseURL}/courses/${courseId}/flag`, { reason }),
};

// Publication Endpoints
export const learnerPublicationAPI = {
  // List publications with filtering and pagination
  getPublications: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    return axios.get(`${baseURL}/publications?${queryParams.toString()}`);
  },

  // Get publication details
  getPublication: (publicationId) => axios.get(`${baseURL}/publications/${publicationId}`),

  // Get preview URL (recommended)
  getPreviewUrl: (publicationId) => axios.get(`${baseURL}/publications/${publicationId}/preview`),

  // Stream publication (inline, no-store)
  getStreamUrl: (publicationId) => `${BASE_URL}${baseURL}/publications/${publicationId}/stream`,
};

// Download Endpoints
export const learnerDownloadAPI = {
  // Get download URL for video or file
  getDownloadUrl: (resourceType, resourceId) => 
    axios.get(`${baseURL}/download/${resourceType}/${resourceId}`),
  
  // Get video URL for viewing
  getVideoUrl: (videoId) => 
    axios.get(`${baseURL}/videos/${videoId}/url`),
  
  // Get file URL for preview
  getFileUrl: (fileId) => 
    axios.get(`${baseURL}/files/${fileId}/url`),
  
  // Get file preview
  getFilePreview: (fileId) => 
    axios.get(`${baseURL}/files/${fileId}/preview`),
};

// Progress Tracking Endpoints
export const learnerProgressAPI = {
  // Get course progress with detailed progress data
  getCourseProgress: (courseId) => axios.get(`${baseURL}/courses/${courseId}/progress`),
  
  // Update video progress
  updateVideoProgress: (videoId, progressData) => 
    axios.post(`${baseURL}/videos/${videoId}/progress`, {
      current_time: progressData.current_time,
      duration: progressData.duration,
      progress_percentage: progressData.progress_percentage,
      completed: progressData.completed
    }),
  
  // Toggle item completion
  toggleCompletion: (itemType, itemId, isCompleted) => 
    axios.post(`${baseURL}/progress/toggle-completion`, { 
      item_type: itemType, 
      item_id: itemId, 
      completed: isCompleted 
    }),
  
  // Get file attachments for a video
  getVideoFiles: (videoId) => axios.get(`${baseURL}/videos/${videoId}/files`),
};

// Quiz Endpoints
export const learnerQuizAPI = {
  // Get quiz with questions
  getQuiz: (quizId) => axios.get(`${baseURL}/quizzes/${quizId}`),
  
  // Submit quiz
  submitQuiz: (quizId, answers, timeTaken) => 
    axios.post(`${baseURL}/quizzes/${quizId}/submit`, { answers, time_taken: timeTaken }),
  
  // Get quiz attempts
  getQuizAttempts: (quizId = null) => {
    const params = quizId ? { quiz_id: quizId } : {};
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    return axios.get(`${baseURL}/quiz-attempts?${queryParams.toString()}`);
  },
};

// Rating Endpoints
export const learnerRatingAPI = {
  // Rate teacher
  rateTeacher: (teacherId, rating, courseId, review = '') => 
    axios.post(`${baseURL}/teachers/${teacherId}/rate`, { rating, course_id: courseId, review }),
};

// Flagging Endpoints
export const learnerFlagAPI = {
  // Get my flags
  getMyFlags: () => axios.get(`${baseURL}/flags`),
};

// Dashboard Endpoints
export const learnerDashboardAPI = {
  // Get learner dashboard
  getDashboard: () => axios.get(`${baseURL}/dashboard`),
};

// Utility Endpoints
export const learnerUtilityAPI = {
  // Get all grades
  getGrades: () => axios.get(`${baseURL}/grades`),
  // Get all subjects
  getSubjects: () => axios.get(`${baseURL}/subjects`),
};

// AI Endpoints
export const learnerAIAPI = {
  // Get quiz review with detailed results
  getQuizReview: (quizAttemptId) => axios.get(`/ai/quiz-attempts/${quizAttemptId}/review`),
  
  // Get AI explanation for a question
  getAIExplanation: (questionId, quizAttemptId) => 
    axios.get(`/ai/questions/${questionId}/ai-explanation`, {
      params: { quiz_attempt_id: quizAttemptId }
    }),
};

export default {
  learnerBadgeAPI,
  learnerCourseAPI,
  learnerPublicationAPI,
  learnerDownloadAPI,
  learnerProgressAPI,
  learnerQuizAPI,
  learnerRatingAPI,
  learnerFlagAPI,
  learnerDashboardAPI,
  learnerUtilityAPI,
  learnerAIAPI,
};
